import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clock } from "lucide-react";
import {
  DETAIL_SCHEDULING_INCOMPLETE_LABEL,
  TIME_NOT_SET_LABEL,
  parseValidArrivalWindow,
  validateStaffRescheduleForm,
} from "@shared/svrSchedulingIntegrity";

export function formatSvrDetailArrivalWindow(slot: string | null | undefined): string {
  const valid = parseValidArrivalWindow(slot);
  if (!valid) return TIME_NOT_SET_LABEL;
  const legacyMap: Record<string, string> = {
    "9am-10am": "9:00am – 10:00am",
    "10am-11am": "10:00am – 11:00am",
    "11am-12pm": "11:00am – 12:00pm",
    "2pm-3pm": "2:00pm – 3:00pm",
    "3pm-4pm": "3:00pm – 4:00pm",
    "4pm-5pm": "4:00pm – 5:00pm",
  };
  if (legacyMap[valid]) return legacyMap[valid];
  const dashIdx = valid.indexOf("-", valid.indexOf(":") + 1);
  const s = dashIdx >= 0 ? valid.slice(0, dashIdx) : valid;
  const e = dashIdx >= 0 ? valid.slice(dashIdx + 1) : "";
  const fmt = (t: string) => {
    const suffix = t.endsWith("am") ? "am" : t.endsWith("pm") ? "pm" : null;
    const clean = suffix ? t.slice(0, -2) : t;
    const [hStr, mStr] = clean.split(":");
    let h = Number(hStr);
    const m = Number(mStr ?? 0);
    if (isNaN(h) || isNaN(m)) return t;
    if (suffix === "pm" && h !== 12) h += 12;
    if (suffix === "am" && h === 12) h = 0;
    const ampm = h < 12 ? "am" : "pm";
    const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${h12}:${String(m).padStart(2, "0")}${ampm}`;
  };
  return s && e ? `${fmt(s)} – ${fmt(e)}` : valid;
}

export function clockInputsFromArrivalWindow(
  window: string | null | undefined,
): { start: string; end: string } {
  const valid = parseValidArrivalWindow(window);
  if (!valid) return { start: "", end: "" };
  if (/^\d{2}:\d{2}-\d{2}:\d{2}$/.test(valid)) {
    const [s, e] = valid.split("-");
    return { start: s ?? "", end: e ?? "" };
  }
  return { start: "", end: "" };
}

export type SvrVisitScheduleFormProps = {
  arrivalWindow?: string | null;
  canEdit: boolean;
  showForm: boolean;
  rescheduleDate: string;
  rescheduleStart: string;
  rescheduleEnd: string;
  pending: boolean;
  onOpenForm: () => void;
  onCloseForm: () => void;
  onDateChange: (value: string) => void;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  onConfirm: (arrivalWindow: string) => void;
  onInvalid: (message: string) => void;
};

export function SvrVisitScheduleAlert(props: SvrVisitScheduleFormProps) {
  const incomplete = !parseValidArrivalWindow(props.arrivalWindow);
  const actionLabel = incomplete ? "Set visit time" : "Reschedule";

  return (
    <div data-testid="svr-visit-schedule-details">
      {incomplete ? (
        <div
          role="alert"
          className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950"
        >
          <p className="font-semibold">{DETAIL_SCHEDULING_INCOMPLETE_LABEL}</p>
          <p className="mt-1 text-xs">
            This visit has a date but no start/end time, so it cannot appear in a calendar slot.
          </p>
          {props.canEdit && !props.showForm ? (
            <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={props.onOpenForm}>
              {actionLabel}
            </Button>
          ) : null}
        </div>
      ) : null}

      {props.canEdit && props.showForm ? (
        <div className="mb-4 space-y-3 rounded-md border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-medium text-amber-800">{incomplete ? "Set visit time" : "Reschedule Visit"}</p>
          <p className="text-[11px] text-amber-900">
            Confirming saves the date and time. Assigned staff may be notified by the existing
            reschedule workflow. Nothing is sent until you confirm.
          </p>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs text-muted-foreground">New Date</Label>
              <Input
                type="date"
                value={props.rescheduleDate}
                onChange={(e) => props.onDateChange(e.target.value)}
                className="mt-1 h-8 text-sm"
                aria-label="New Date"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Start Time</Label>
              <Input
                type="time"
                value={props.rescheduleStart}
                onChange={(e) => props.onStartChange(e.target.value)}
                className="mt-1 h-8 text-sm"
                aria-label="Start Time"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">End Time</Label>
              <Input
                type="time"
                value={props.rescheduleEnd}
                onChange={(e) => props.onEndChange(e.target.value)}
                className="mt-1 h-8 text-sm"
                aria-label="End Time"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="h-7 flex-1 text-xs" onClick={props.onCloseForm}>
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-7 flex-1 bg-black text-xs text-white hover:bg-gray-800"
              disabled={props.pending}
              onClick={() => {
                const result = validateStaffRescheduleForm({
                  date: props.rescheduleDate,
                  start: props.rescheduleStart,
                  end: props.rescheduleEnd,
                });
                if (!result.ok) {
                  props.onInvalid(result.error);
                  return;
                }
                props.onConfirm(result.arrivalWindow);
              }}
            >
              {props.pending ? "Saving…" : "Confirm Reschedule"}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function SvrVisitTimeRow(props: {
  arrivalWindow?: string | null;
  canEdit: boolean;
  showForm: boolean;
  onOpenForm: () => void;
}) {
  const incomplete = !parseValidArrivalWindow(props.arrivalWindow);
  const timeLabel = formatSvrDetailArrivalWindow(props.arrivalWindow);
  return (
    <tr data-testid="svr-visit-time-row">
      <td className="w-36 py-1.5 text-muted-foreground">Time</td>
      <td className="py-1.5">
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3 w-3" />
          <span data-testid="svr-visit-time-value">{incomplete ? `Time: ${TIME_NOT_SET_LABEL}` : timeLabel}</span>
        </span>
        {props.canEdit && !props.showForm && !incomplete ? (
          <Button size="sm" variant="outline" className="ml-2 h-7 text-xs" onClick={props.onOpenForm}>
            Reschedule
          </Button>
        ) : null}
      </td>
    </tr>
  );
}
